import { useCallback, useEffect, useRef, useState } from 'react';
import {
  closeTableSession,
  openTableSession,
  staffDraftRemoveLine,
  staffDraftUpsertLine,
  upsertTableSessionCart,
} from '../../../hospitality/infra/hospitalityRepo';
import { takeTablesideCheckoutBaseline } from '../../../hospitality/domain/tablesideCheckoutBaseline';
import { consumeTablesidePosHandoff } from '../../../hospitality/domain/tablesidePosHandoff';
import { mapStoredCartLineToCartItem } from '../../../hospitality/application/buildTableCheckoutCart';
import { captureCatch } from '../../../shared/sentry/sentryUser';
import { usePosStore, type CartItem } from '../../store/usePosStore';
import { usePosCheckoutStore } from '../../store/usePosCheckoutStore';
import { parseTipIntent } from '../../application/posCheckoutTip';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { isPosStaffTipsEnabled } from '../../application/posStaffTips';
import { useBusinessProfileStore } from '../../../shell/session/store/useBusinessProfileStore';

function coerceCartJson(raw: unknown): CartItem[] {
  if (Array.isArray(raw)) {
    return raw
      .map(mapStoredCartLineToCartItem)
      .filter((line): line is CartItem => line != null);
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as unknown;
      return Array.isArray(parsed)
        ? parsed
            .map(mapStoredCartLineToCartItem)
            .filter((line): line is CartItem => line != null)
        : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Cuenta abierta (POS-S3 / POS-S4 keep cart): hidrata carrito desde table_session
 * y persiste cambios con debounce mientras la sesión sigue abierta.
 *
 * Importante: no pisar un cart_json con ítems escribiendo [] durante la hidratación
 * (carrera clásica al abrir desde Mesas).
 */
export function usePosTableSession(options: {
  currentBranchId: string | null | undefined;
  diningTableId: string | null | undefined;
  /** Cambia en cada “Ir a cobrar” para forzar hydrate. */
  openKey?: string | null;
  /**
   * Cobrar desde Mesas con Pendiente: el carrito ya viene mergeado en el store.
   * No pisar con cart_json del server ni persistir hasta cobrar (evita volcar Pendiente sin venta).
   */
  checkoutPreview?: boolean;
}) {
  const { currentBranchId, diningTableId, openKey, checkoutPreview = false } = options;
  const sessionIdRef = useRef<string | null>(null);
  const hydrateGenRef = useRef(0);
  const hydratingRef = useRef(false);
  const hydratedCountRef = useRef(0);
  const allowEmptyPersistRef = useRef(false);
  const checkoutPreviewModeRef = useRef(false);
  /** Pausa el persist debounced mientras un split-bill está en vuelo (el cobro decide el contenido final). */
  const cartPersistPausedRef = useRef(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionCovers, setSessionCovers] = useState<number | null>(null);
  const [sessionOpenedAt, setSessionOpenedAt] = useState<string | null>(null);
  const replaceCart = usePosStore((s) => s.replaceCart);
  const cart = usePosStore((s) => s.cart);
  const setTipIntent = usePosCheckoutStore((s) => s.setTipIntent);
  const setPrimaryStaffUserId = usePosCheckoutStore((s) => s.setPrimaryStaffUserId);
  const setTipUiEnabled = usePosCheckoutStore((s) => s.setTipUiEnabled);
  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const posStaffTipsEnabled = isPosStaffTipsEnabled(businessProfile);

  useEffect(() => {
    sessionIdRef.current = null;
    setSessionReady(false);
    setSessionCovers(null);
    setSessionOpenedAt(null);
    hydratingRef.current = true;
    allowEmptyPersistRef.current = false;
    cartPersistPausedRef.current = false;
    if (!diningTableId || !currentBranchId) {
      hydratingRef.current = false;
      hydratedCountRef.current = 0;
      checkoutPreviewModeRef.current = false;
      setTipUiEnabled(false);
      setPrimaryStaffUserId(null);
      return;
    }

    const handoff = consumeTablesidePosHandoff({
      branchId: currentBranchId,
      diningTableId,
      openKey,
    });
    const effectiveCheckoutPreview = checkoutPreview || handoff?.checkoutPreview === true;
    checkoutPreviewModeRef.current = effectiveCheckoutPreview;

    const gen = ++hydrateGenRef.current;
    let cancelled = false;
    void (async () => {
      try {
        const session = await openTableSession({
          branchId: currentBranchId,
          diningTableId,
        });
        if (cancelled || gen !== hydrateGenRef.current) return;
        sessionIdRef.current = session.id;
        setSessionCovers(session.covers);
        setSessionOpenedAt(session.openedAt ?? null);
        setTipUiEnabled(posStaffTipsEnabled);
        setPrimaryStaffUserId(session.assignedUserId ?? null);
        const preloadedTip = parseTipIntent(session.tipIntent);
        if (posStaffTipsEnabled && preloadedTip && preloadedTip.amount > 0) {
          setTipIntent(preloadedTip);
        } else {
          setTipIntent({ amount: 0, percent: null, source: 'staff' });
        }
        const handoffCart = (handoff?.cart ?? []) as CartItem[];
        if (handoffCart.length > 0) {
          replaceCart(handoffCart);
          hydratedCountRef.current = handoffCart.length;
        } else if (effectiveCheckoutPreview) {
          // Conservar carrito mergeado (cuenta + pendiente) que armó Mesas.
          hydratedCountRef.current = usePosStore.getState().cart.length;
        } else {
          const nextCart = coerceCartJson(session.cartJson);
          const currentLen = usePosStore.getState().cart.length;
          if (nextCart.length === 0 && currentLen > 0) {
            hydratedCountRef.current = currentLen;
          } else {
            hydratedCountRef.current = nextCart.length;
            replaceCart(nextCart);
          }
        }
        setSessionReady(true);
      } catch (error) {
        captureCatch('pos', 'open-table-session')(error);
        if (!cancelled && gen === hydrateGenRef.current) setSessionReady(false);
      } finally {
        if (!cancelled && gen === hydrateGenRef.current) {
          hydratingRef.current = false;
        }
      }
    })();

    return () => {
      cancelled = true;
      hydratingRef.current = true;
    };
  }, [
    checkoutPreview,
    currentBranchId,
    diningTableId,
    openKey,
    replaceCart,
    setPrimaryStaffUserId,
    setTipIntent,
    setTipUiEnabled,
    posStaffTipsEnabled,
  ]);

  useEffect(() => {
    if (checkoutPreviewModeRef.current) return;
    if (!sessionReady || !sessionIdRef.current || hydratingRef.current) return;
    const sessionId = sessionIdRef.current;
    const gen = hydrateGenRef.current;
    const timer = setTimeout(() => {
      if (gen !== hydrateGenRef.current || hydratingRef.current || cartPersistPausedRef.current) return;
      const latest = usePosStore.getState().cart;
      // No pisar la cuenta del servidor con carrito vacío post-hidratación.
      if (
        latest.length === 0 &&
        hydratedCountRef.current > 0 &&
        !allowEmptyPersistRef.current
      ) {
        return;
      }
      if (latest.length === 0) {
        allowEmptyPersistRef.current = false;
      } else {
        hydratedCountRef.current = latest.length;
      }
      void upsertTableSessionCart({
        sessionId,
        cartJson: latest,
      }).catch(captureCatch('pos', 'table-session-cart-save'));
    }, 500);
    return () => clearTimeout(timer);
  }, [cart, checkoutPreview, sessionReady, openKey]);

  const getTableSessionId = useCallback(() => sessionIdRef.current, []);

  const closeActiveTableSession = useCallback(async (saleId?: string | null) => {
    const sessionId = sessionIdRef.current;
    if (!sessionId) return;
    cartPersistPausedRef.current = false;
    const soldCart = usePosStore.getState().cart;
    // Snapshot de lo cobrado: sin esto, checkoutPreview deja cart_json viejo y
    // la ficha muestra ítems que no entraron en la venta.
    if (saleId) {
      await upsertTableSessionCart({
        sessionId,
        cartJson: soldCart,
      }).catch(captureCatch('pos', 'table-session-cart-before-close'));
    }
    await closeTableSession({ sessionId, saleId: saleId ?? null });
    sessionIdRef.current = null;
    setSessionReady(false);
    if (saleId && currentBranchId && diningTableId) {
      const baseline = takeTablesideCheckoutBaseline() as CartItem[];
      const soldIds = new Set(
        soldCart.map((l) => l.lineId).filter((id): id is string => Boolean(id)),
      );
      // Solo consumir líneas del snapshot de Cobrar. Lo que el guest sumó después
      // (postre) no está en baseline → queda en Pendiente.
      for (const line of baseline) {
        const lineId = line.lineId;
        if (!lineId) continue;
        if (soldIds.has(lineId)) {
          await staffDraftRemoveLine({
            branchId: currentBranchId,
            diningTableId,
            clientLineId: lineId,
          }).catch(captureCatch('pos', 'tableside-draft-remove-sold'));
          continue;
        }
        // Sacado del POS antes de cobrar → vuelve / queda en Pendiente.
        await staffDraftUpsertLine({
          branchId: currentBranchId,
          diningTableId,
          clientLineId: lineId,
          productId: line.productId || '',
          name: line.name,
          qty: line.quantity,
          unitPrice: line.price,
          unitNote: line.lineNote ?? null,
          optionsJson: line.menuOptions ?? [],
          optionsLabel: null,
        }).catch(captureCatch('pos', 'tableside-restore-unsold-to-draft'));
      }
    } else {
      takeTablesideCheckoutBaseline();
    }
  }, [currentBranchId, diningTableId]);

  /** Marca que el próximo persist vacío es intencional (Vaciar carrito). */
  const allowEmptyCartPersist = useCallback(() => {
    allowEmptyPersistRef.current = true;
    hydratedCountRef.current = 0;
  }, []);

  /** Split-bill: el carrito POS (items seleccionados) no es la cuenta del server hasta cobrar. */
  const pauseCartPersist = useCallback(() => {
    cartPersistPausedRef.current = true;
  }, []);
  const resumeCartPersist = useCallback(() => {
    cartPersistPausedRef.current = false;
  }, []);

  return {
    getTableSessionId,
    closeActiveTableSession,
    allowEmptyCartPersist,
    pauseCartPersist,
    resumeCartPersist,
    sessionReady,
    sessionCovers,
    sessionOpenedAt,
  };
}
